import request from 'sb-request'
import actions from 'redux/actions'
import merge from 'deepmerge'
import { getState, reducers } from 'redux/core'
import {
  constants, deferredPromise, fbq, ga, links,
  mixpanel, sentry, snowplow, object, dataLayer, isNotEmpty,
} from 'common/helpers'
import {
  modifyAbTests, modifyClosedPopups, modifySubscription,
  modifyTokens,
} from 'common/helpers/modifiers/modifyResponse'
import getAbOverrides from 'helpers/getters/getAbOverrides'
import getGqlQuery from 'common/helpers/getters/getGqlQuery'
import checkAccessToMobileNavExperiment from 'common/helpers/getters/checkAccessToMobileNavExperiment'
import { closedPopupsQuery, meQuery, redeemCouponQuery, subscriptionQuery } from 'common/queries'
import { getCookieValue, getFeatureToggle } from 'helpers'

import messages from './messages'


/**
 * Check if user with passed email already exists in system
 *
 * @param {String} value - email
 * @returns {Promise} request
 */
const checkExistByEmail = (value) => {
  const email = value.trim().toLowerCase()

  if (!email) {
    return Promise.reject()
  }

  return request.get('rest/user/exists', {
    query: {
      email,
    },
  })
    .then(({ exists, gender }) => {
      console.log('check user exist by email result: ', { exists, gender })
      return exists
    })
}

const bindCouponToUser = () => {
  const {
    models: { subscription: { isNotSubscribed = true, subscriptionDate } = {} },
  } = getState()
  const { code, offerType } = getCookieValue(constants.cookieNames.giftCoupon) || {}

  if (!code) {
    return Promise.resolve()
  }

  // Marketing coupons should be bound only for new subscribers
  if (!isNotSubscribed || subscriptionDate) {
    actions.misc.cookies.removeItem(constants.cookieNames.giftCoupon)

    actions.models.modals.open(constants.modals.Dialog, {
      contentMessage: messages.dialogModal.message,
      buttonTitle: messages.dialogModal.button,
    })

    return Promise.resolve()
  }

  return request.post('rest/coupon/bind', {
    body: {
      code,
      offerType,
    },
    tokenName: constants.requestTokens.bindPromoCoupon,
  })
    .then((result) => {
      const redeem = actions.models.me.updateRedeemCoupon({
        ...result,
        code,
      })

      actions.misc.cookies.setItem(constants.cookieNames.bindedCoupon, { code, offerType })
      actions.misc.cookies.removeItem(constants.cookieNames.giftCoupon)

      return redeem
    })
}

/**
 * Sets user id for google analytics
 *
 * @param {number} userId
 */
const setUserIdForGoogle = (userId) => {
  ga.set('set', 'userId', userId)
  ga.set('set', '&uid', userId)
  ga.set('set', 'dimension2', userId)
}

/**
 * Identifies user via mixpanel using mixpanel id
 *
 * @param {string} mixpanelId
 */
const handleMixpanelId = (mixpanelId) => {
  if (mixpanelId) {
    mixpanel.identify(mixpanelId)
    mixpanel.people.set()
  }
  else {
    mixpanel.reset()
  }

  actions.misc.cookies.setItem('scnt_mixpanel_clientid', mixpanel.get_distinct_id(), {
    domain: '.scentbird.com',
    expires: 365,
  })
}

/**
 * Set super property for ab-test 'site:mobileNavigation' SB-75
 */
const setMobileNavExperimentProp = () => {
  const { models: { me: { isLoggedIn, registrationDate } } } = getState()

  const hasAccess = checkAccessToMobileNavExperiment({ isMobile: window.__globals__.device.isMobile, isLoggedIn, registrationDate })

  mixpanel.register({
    'Audience ab navigation': hasAccess,
  })
}

const syncMixpanelProps = () => {
  if (typeof window.syncMixpanelSuperPropertiesCookie === 'function') {
    window.syncMixpanelSuperPropertiesCookie()
  }
}


let isTimeoutCalled = false

const setNewrelicContext = (userId, email) => {
  if (object.get(window, 'newrelic.interaction')) {
    window.newrelic.interaction()
      .setAttribute('userId', userId)
      .setAttribute('email', email)
  }
  else if (!isTimeoutCalled) {
    setTimeout(() => {
      isTimeoutCalled = true

      setNewrelicContext(userId, email)
    }, 15000)
  }
}

const setUserContext = ({ userId, email, mixpanelId }) => {
  snowplow.setUserId(userId)
  setUserIdForGoogle(userId)
  handleMixpanelId(mixpanelId)
  sentry.setUserContext({ id: userId, email, mixpanelId })
  actions.models.user.setNewrelicContext(userId, email)
}

const trackRegisterEvents = () => {
  dataLayer.push({ event: 'signup' })
  fbq.track('CompleteRegistration')
  snowplow.track(constants.snowplowEvents.signup, { type: 'common' }, false)
}

/**
 * Saves new split tests values to globals, tries to re-register mixpanel properties.
 */
const updateAbTests = (abTests) => {
  if (typeof window.__updateGlobals__ === 'function') {
    window.__updateGlobals__({ 'abTests': merge(abTests, getAbOverrides()) })
  }

  // Update mixpanel properties
  if (typeof window.registerMixpanel === 'function') {
    window.registerMixpanel()
  }
}

const modifyUserData = (data) => {
  const {
    abTests,
    tokens,
    redeemCoupon,
    config: { vindiciaUrl },
    user: { me, subscription, closedPopups },
  } = data

  if (!Boolean(subscription)) {
    sentry.captureException('subscription is null', {
      extra: data,
    })
  }

  return {
    abTests: modifyAbTests(abTests),
    tokens: modifyTokens(tokens),
    // Sometimes closedPopups is null
    closedPopups: modifyClosedPopups(isNotEmpty(closedPopups) ? closedPopups : []),
    subscription: modifySubscription(Boolean(subscription) ? subscription : { status: {} }),
    me,
    redeemCoupon,
    vindiciaUrl,
  }
}

const fetchCommonData = (awaitFor = Promise.resolve()) =>
  request.post('graphql?name=userCommonData', {
    body: {
      query: `{
        abTests {
          name
          value
          mixpanelProp
        }
        tokens {
          name
          value
        }
        ${redeemCouponQuery}
        config {
          vindiciaUrl: vindiciaCreditCardUrl
        }
      }`,
    },
    modifyResult: (data) => {
      const {
        abTests,
        tokens,
        redeemCoupon,
        config: { vindiciaUrl },
      } = data

      return {
        abTests: modifyAbTests(abTests),
        tokens: modifyTokens(tokens),
        redeemCoupon,
        vindiciaUrl,
      }
    },
  })
    .then((data) => {
      awaitFor.then(() => {
        const { abTests, tokens, redeemCoupon, vindiciaUrl } = data

        updateAbTests(abTests)
        actions.models.requestTokens.set(tokens)
        actions.misc.vindiciaUrl.set(vindiciaUrl)
        actions.models.me.updateRedeemCoupon(redeemCoupon)

        setMobileNavExperimentProp()
        syncMixpanelProps()
      })
    })

// Todo remove this after implementation register graphQl
/**
 * Fetches all user data after login and register
 *
 * @param {Promise} awaitFor
 * @returns {Promise}
 */
const fetchUserData = (awaitFor) => {
  actions.models.ui.showRequestLoader()

  return request.post('graphql?name=userDataAfterLogin', {
    body: {
      query: `{
        abTests {
          name
          value
          mixpanelProp
        }
        tokens {
          name
          value
        }
        ${redeemCouponQuery}
        config {
          vindiciaUrl: vindiciaCreditCardUrl
        }
        user {
          ${meQuery}
          ${subscriptionQuery}
          ${closedPopupsQuery}
        }
      }`,
    },
    modifyResult: modifyUserData,
  })
    .then(
      ({ abTests, tokens, closedPopups, vindiciaUrl, redeemCoupon, me, subscription }) => {
        awaitFor.then(() => {
          actions.models.me.set(me)
          updateAbTests(abTests)
          actions.models.requestTokens.set(tokens)
          actions.misc.vindiciaUrl.set(vindiciaUrl)
          actions.models.ui.setClosedPopups(closedPopups)
          actions.models.me.updateRedeemCoupon(redeemCoupon)
          actions.models.subscription.set(subscription)

          setMobileNavExperimentProp()
          syncMixpanelProps()

          actions.models.ui.hideRequestLoader()
        })
      },
      () => {
        actions.models.ui.hideRequestLoader()
      },
    )
}

/**
 * Check Auth Errors
 * @param responseData
 * @returns {Promise<Promise<never>|undefined>}
 */
const checkErrors = async ({ responseData }) => {
  if (responseData.errors) {
    return Promise.reject(responseData)
  }
}

/**
 * Call Auth Error Processing
 * @param title {String}
 * @param [error] {Object}
 * @param [errorCallback] {Function}
 * @param [errors] {Array}
 * @param [token] {String}
 * @returns {Promise<Promise<never>>}
 */
const callErrorProcessing = async ({ title, error, errorCallback, errors, token, input }) => {
  if (errors) {
    errors.forEach((errorData) => {
      const { error, message, timestamp, additionalFields }  = errorData
      // TODO added on 2019-08-21 by @vkudla uncomment wrong token error when https://scentbird.atlassian.net/browse/SB-238 will be ready
      if (!['no_user_or_incorrect_password', 'wrong_token'].includes(error)) {
        sentry.captureException(`${title} failed`, {
          extra: {
            error,
            input,
            message,
            timestamp,
            additionalFields,
          },
        })
      }
    })
  }
  else {
    sentry.captureException(title, {
      extra: {
        error,
        input,
      },
    })
  }

  if (typeof errorCallback === 'function') {
    errorCallback()
  }
  else {
    actions.models.modals.open(constants.modals.SomethingWentWrong)
  }

  return Promise.reject({ token })
}

/**
 * Update Token on promise reject
 * @param errorData
 * @returns {Promise<never>}
 */
const handleRejectUpdateToken = (errorData) => {
  const { token, tokenName } = errorData

  if (token) {
    actions.models.requestTokens.update({ [tokenName]: token })
  }

  return Promise.reject(errorData)
}

/**
 * login Gql
 * @param email {String}
 * @param password {String}
 * @param token {String}
 * @param errorCallback {Function}
 * @returns {Promise<{authenticationValid: *, subscriptionProgress: *, me: *, realEmail: *, subscription: *, closedPopups: *, token: *}>}
 */
const loginGql = async ({ username: email, password, token }, errorCallback) => {
  try {
    const responseData = await request.post('graphql?name=loginGql', {
      body: {
        query: `
        mutation {
          ${getGqlQuery('login', { input: { email, password, token } })} {
            user {
              ${meQuery}
              ${subscriptionQuery}
              ${closedPopupsQuery}
              subscriptionProgress {
                status
                subscriptionSessionId
              }
            }
            token
            result
            errors {
              error
              message
              timestamp
              additionalFields
            }
          }
        }
      `,
      },
    })

    await checkErrors({ responseData: responseData.login })

    return getUserData(responseData.login)
  }
  catch (error) {
    const token = error.token || object.get(error, 'data.login.token')

    await callErrorProcessing({
      ...error,
      token,
      title: 'loginGql',
      errorCallback,
      input: {
        email,
        token,
      },
    })
  }
}

/**
 * Authorize user by email and password
 *
 * @param {string} email
 * @param {string} password
 * @param {boolean} isLoginGQLEnabled
 * @param {function} [errorCallback]
 * @returns {Promise} request
 */
const login = ({ email, password, isLoginGQLEnabled }, errorCallback) => {
  const { models: { requestTokens: { signin: signinToken } } } = getState()

  actions.models.ui.showRequestLoader()

  const body = {
    username: email.trim().toLowerCase(),
    password,
    token: signinToken,
  }

  if (isLoginGQLEnabled) {
    return loginGql(body, errorCallback)
      .then((data) => {
        const {
          me, subscription, closedPopups, subscriptionProgress, token,
          me: { id, email, mixpanelClientUid },
        } = data

        actions.models.requestTokens.update({ [constants.requestTokens.signin]: token })

        setUserContext({ userId: id, email, mixpanelId: mixpanelClientUid })

        // Update store only after all requests
        const [ awaitFor, awaitForResolve, awaitForReject ] = deferredPromise()

        awaitFor.then(() => {
          actions.models.me.set(me)
          actions.models.ui.setClosedPopups(closedPopups)
          actions.models.subscription.set(subscription)
          reducers.models.payment.update({ subscriptionProgress })
        })

        Promise.all([
          actions.models.user.fetchCommonData(awaitFor),
          actions.view.cosmeticsPage.fetchNotEmptyTypes(awaitFor),
        ])
          .then(awaitForResolve, awaitForReject)

        return awaitFor
      }, (errorData) => handleRejectUpdateToken({
        ...errorData,
        tokenName: constants.requestTokens.signin,
      }))
      .finally(() => actions.models.ui.hideRequestLoader())
  }

  // TODO remove thia logic after 10 days of work on the graphQl
  return request.post('login/authenticate', {
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    },
    body,
    formData: true,
    withErrorModal: false,
    onComplete: () => actions.models.ui.hideRequestLoader(),
  })
    .then(({ error, user: userId, mixpanelId }) => {
      if (error) {
        errorCallback()

        return Promise.reject()
      }

      setUserContext({ userId, email, mixpanelId })
    })
    .then(() => {
      const [ awaitFor, awaitForResolve, awaitForReject ] = deferredPromise()

      Promise.all([
        actions.models.user.fetchUserData(awaitFor),
        actions.view.cosmeticsPage.fetchNotEmptyTypes(awaitFor),
        actions.models.cart.addSubscription(false),
        actions.models.payment.fetchPostponedSubscriptionProcessing(),
      ])
        .then(awaitForResolve, awaitForReject)

      return awaitFor
    })
}

const registerGql = async ({
  email,
  gender,
  password,
  passwordStrength,
  token,
  products,
  receiveUpdates,
}) => {
  const input = {
    email,
    gender: {
      isEnum: true,
      value: gender,
    },
    password,
    passwordStrength: {
      isEnum: true,
      value: passwordStrength,
    },
    token,
    products,
    receiveUpdates,
  }

  try {
    const responseData = await request.post('graphql?name=registerGql', {
      body: {
        query: `
        mutation {
          ${getGqlQuery('register', { input })} {
            user {
              ${meQuery}
              ${subscriptionQuery}
            }
            token
            result
            errors {
              error
              message
              timestamp
              additionalFields
            }
          }
        }
      `,
      },
    })

    await checkErrors({ responseData: responseData.register })

    return getUserData(responseData.register)
  }
  catch (error) {
    const token = error.token || object.get(error, 'data.register.token')

    await callErrorProcessing({
      ...error,
      token,
      title: 'registerGql',
      input: {
        email,
        token,
      },
    })
  }
}

/**
 * Register new user
 *
 * @param {Object} opts
 * @param {Object} opts.values
 * @param {String} opts.values.name
 * @param {String} opts.values.email
 * @param {String} opts.values.gender
 * @param {String} opts.values.password
 * @param {String} opts.values.passwordStrength
 * @param {String} [opts.values.token]
 * @param {function[]} opts.postponeActions
 * @returns {Promise} request
 */
const register = ({ values, postponeActions = [], isRegisterGqlEnabled }) => {
  const { models: { requestTokens: { registration: registrationToken } } } = getState()
  const { email } = values

  actions.models.ui.showRequestLoader()
  const products = actions.view.pickPage.getSelectedItemsIdsFromStorage()

  const body = {
    ...values,
    products,
    token: registrationToken,
  }

  if (isRegisterGqlEnabled) {
    return registerGql(body)
      .then((data) => {
        const {
          me, subscription, closedPopups, token,
          me: { id, email, mixpanelClientUid },
        } = data

        actions.models.requestTokens.update({ [constants.requestTokens.registration]: token })

        trackRegisterEvents()
        setUserContext({ userId: id, email, mixpanelId: mixpanelClientUid })

        // Update store only after all requests
        const [ awaitFor, awaitForResolve, awaitForReject ] = deferredPromise()

        awaitFor.then(() => {
          actions.models.me.set(me)
          actions.models.ui.setClosedPopups(closedPopups)
          actions.models.subscription.set(subscription)
        })

        Promise.all([
          actions.models.user.fetchCommonData(awaitFor),
          actions.view.cosmeticsPage.fetchNotEmptyTypes(awaitFor),
        ])
          .then(awaitForResolve, awaitForReject)

        return awaitFor
      }, (errorData) => handleRejectUpdateToken({
        ...errorData,
        tokenName: constants.requestTokens.registration,
      }))
  }
  // TODO remove thia logic after 10 days of work on the graphQl
  return request.post('rest/user/register', {
    body,
    tokenName: constants.requestTokens.registration,
    modifyResult: ({ user: userId, mixpanelId } = {}) => ({ userId, mixpanelId }),
  })
    .then(({ userId, mixpanelId }) => {
      const [ awaitFor, awaitForResolve, awaitForReject ] = deferredPromise()

      // TODO Create one graphql request instead of five below + add fetchNotEmptyTypes from postponeActions
      Promise.all([
        actions.models.user.fetchUserData(awaitFor),
        actions.models.cart.addSubscription(false),
        ...postponeActions.map((action) => action(awaitFor)),
      ])
        .then(() => {
          trackRegisterEvents()
          setUserContext({ userId, email, mixpanelId })
        })
        .then(awaitForResolve, awaitForReject)

      return awaitFor
    })
}

const trackRegisterFail = (error) => {
  const { code, message } = error
  let type

  if (code && message) {
    type = `[${code}]: ${message}`
  }
  else if (code) {
    type = `[${code}]`
  }
  else if (message) {
    type = message
  }

  mixpanel.track(constants.analyticsEvents.registerFailed, {
    type,
  })
}

const getUserData = (data) => {
  const { realEmail, result, user, token } = data
  const { me, closedPopups, subscription, subscriptionProgress } = user || {}

  return {
    me,
    subscriptionProgress,
    subscription: modifySubscription(Boolean(subscription) ? subscription : { status: {} }),
    closedPopups: modifyClosedPopups(isNotEmpty(closedPopups) ? closedPopups : []),
    realEmail,
    token,
    authenticationValid: result,
  }
}

const facebookGql = async (body) => {
  const input = {
    ...body,
    gender: {
      isEnum: true,
      value: body.gender,
    },
  }

  try {
    const responseData = await request.post('graphql?name=facebookSignin', {
      body: {
        query: `
        mutation {
          ${getGqlQuery('facebookSignin', { input })} {
            user {
              ${meQuery}
              ${subscriptionQuery}
              ${closedPopupsQuery}
              subscriptionProgress {
                status
                subscriptionSessionId
              }
            }
            realEmail
            token
            result
            errors {
              error
              message
              timestamp
              additionalFields
            }
          }
        }
      `,
      },
    })

    await checkErrors({ responseData: responseData.facebookSignin })

    return getUserData(responseData.facebookSignin)
  }
  catch (error) {
    const token = error.token || object.get(error, 'data.facebookSignin.token')

    await callErrorProcessing({
      ...error,
      token,
      title: 'facebookGql',
      input,
    })
  }
}

/**
 * Authorize with Facebook
 *
 * @param {Object} values
 * @param {String} values.tokenName
 * @param {String} [values.email]
 * @param {String} [values.password]
 * @returns {Promise} request
 */
const facebookAuth = (values) => {
  const { email, tokenName, password, isLoginGQLEnabled } = values
  const {
    models: {
      facebook: { auth },
      me: { selectedGender },
      requestTokens,
    },
  } = getState()

  if (!auth) {
    actions.models.modals.open(constants.modals.SomethingWentWrong)
    return Promise.reject()
  }

  const { accessToken, expiresIn, signedRequest, userID } = auth

  if (!accessToken || !signedRequest || !userID) {
    sentry.captureException('FacebookAuth with empty tokens', {
      extra: {
        auth,
      },
    })

    actions.models.modals.open(constants.modals.SomethingWentWrong)
    return Promise.reject()
  }

  const products = actions.view.pickPage.getSelectedItemsIdsFromStorage()

  actions.models.ui.showRequestLoader()

  const body = {
    email,
    password,
    gender: selectedGender,
    products,
    token: requestTokens[tokenName],
    accessToken, // data from FB button authResponse
    expiresIn, // data from FB button authResponse
    signedRequest, // data from FB button authResponse
    userId: userID, // data from FB button authResponse
  }

  const isFacebookEmailRegistrationEnabled = getFeatureToggle(constants.features.facebookEmailRegistration)

  if (isLoginGQLEnabled) {
    return facebookGql(body)
      .then(
        ({
          realEmail,
          authenticationValid,
          me,
          subscription,
          closedPopups,
          subscriptionProgress,
          token,
        }) => {
          actions.models.requestTokens.update({ [tokenName]: token })
          // Facebook email authentication https://scentbird.atlassian.net/browse/SCNT-2988
          // !password means that this action called not from FacebookRegisterModal
          if (!realEmail && !password && isFacebookEmailRegistrationEnabled) {
            // TODO remove logic for saving data in session storage when new register pages will be done
            actions.misc.cookies.setSessionItem(constants.cookieNames.facebookEmailRegistration, true)
            actions.models.router.push(links.facebookEmailRegistration)

            // break promise chain and pass "break" to prevent showing modal in components/modals/FacebookRegisterModal
            return Promise.reject('break')
          }

          // RegisterModal logic
          if (!authenticationValid) {
            return Promise.reject()
          }

          const { id, email, mixpanelClientUid } = me

          setUserContext({ userId: id, email, mixpanelId: mixpanelClientUid })

          // Update store only after all requests
          const [ awaitFor, awaitForResolve, awaitForReject ] = deferredPromise()

          awaitFor.then(() => {
            actions.models.me.set(me)
            actions.models.ui.setClosedPopups(closedPopups)
            actions.models.subscription.set(subscription)
            reducers.models.payment.update({ subscriptionProgress })
          })

          Promise.all([
            actions.models.user.fetchCommonData(awaitFor),
            actions.view.cosmeticsPage.fetchNotEmptyTypes(awaitFor),
          ])
            .then(awaitForResolve, awaitForReject)

          return awaitFor
        }, (errorData) => handleRejectUpdateToken({
          ...errorData,
          tokenName,
        }),
      )
      .then(() => bindCouponToUser())
      .finally(() => actions.models.ui.hideRequestLoader())
  }
  // TODO remove thia logic after 10 days of work on the graphQl
  return request.post('rest/facebook/signin', {
    body,
    tokenName,
    onComplete: () => actions.models.ui.hideRequestLoader(),
  })
    .then((result) => {
      const { realEmail, authenticationValid, mixpanelId, user: userId, isJustRegistered } = result

      if (isJustRegistered) {
        snowplow.track(constants.snowplowEvents.signup, { type: 'facebook' }, false)
      }

      // Facebook email authentication https://scentbird.atlassian.net/browse/SCNT-2988
      // !password means that this action called not from FacebookRegisterModal
      if (!realEmail && !password && isFacebookEmailRegistrationEnabled) {
        // TODO remove logic for saving data in session storage when new register pages will be done
        actions.misc.cookies.setSessionItem(constants.cookieNames.facebookEmailRegistration, true)
        actions.models.router.push(links.facebookEmailRegistration)

        // break promise chain and pass "break" to prevent showing modal in components/modals/FacebookRegisterModal
        return Promise.reject('break')
      }

      // RegisterModal logic
      if (typeof authenticationValid === 'boolean' && !authenticationValid) {
        return Promise.reject()
      }

      setUserContext({ userId, email, mixpanelId })

      return result
    })
    .then((result) => {
      const { isJustRegistered } = result

      const [ awaitFor, awaitForResolve, awaitForReject ] = deferredPromise()

      Promise.all([
        actions.models.user.fetchUserData(awaitFor),
        actions.models.cart.addSubscription(false),
        !isJustRegistered ? actions.models.payment.fetchPostponedSubscriptionProcessing() : null,
      ])
        .then(awaitForResolve, awaitForReject)

      return awaitFor
    })
    .then(() => bindCouponToUser())
}

const googleAuthGql = async (body) => {
  const input = {
    ...body,
    gender: {
      isEnum: true,
      value: body.gender,
    },
  }

  try {
    const responseData = await request.post('graphql?name=googleSignin', {
      body: {
        query: `
        mutation {
          ${getGqlQuery('googleSignin', { input })} {
            user {
              ${meQuery}
              ${subscriptionQuery}
              ${closedPopupsQuery}
              subscriptionProgress {
                status
                subscriptionSessionId
              }
            }
            realEmail
            token
            result
            errors {
              error
              message
              timestamp
              additionalFields
            }
          }
        }
      `,
      },
    })

    await checkErrors({ responseData: responseData.googleSignin })

    return getUserData(responseData.googleSignin)
  }
  catch (error) {
    const token = error.token || object.get(error, 'data.googleSignin.token')

    await callErrorProcessing({
      ...error,
      token,
      title: 'googleAuthGql',
      input,
    })
  }
}

/**
 * Authorize with Google
 *
 * @param {Object} values
 * @param {string} values.access_token
 * @param {string} values.id_token
 * @param {number} values.expires_in
 * @returns {Promise} request
 */
const googleAuth = (values) => {
  const { models: { me: { selectedGender }, requestTokens: { googleSignin: googleSigninToken } } } = getState()
  const { access_token: accessToken, id_token: idToken, expires_in: expiresIn, isLoginGQLEnabled } = values

  const products = actions.view.pickPage.getSelectedItemsIdsFromStorage()

  actions.models.ui.showRequestLoader()

  const body = {
    token: googleSigninToken,
    accessToken,
    idToken,
    gender: selectedGender,
    products,
  }

  if (isLoginGQLEnabled) {
    return googleAuthGql(body)
      .then(
        ({
          me,
          subscription,
          closedPopups,
          subscriptionProgress,
          token,
        }) => {
          const { id, email, mixpanelClientUid } = me

          actions.models.requestTokens.update({
            [constants.requestTokens.googleSignin]: token,
          })
          setUserContext({ userId: id, email, mixpanelId: mixpanelClientUid })

          // Update store only after all requests
          const [ awaitFor, awaitForResolve, awaitForReject ] = deferredPromise()

          awaitFor.then(() => {
            actions.models.me.set(me)
            actions.models.ui.setClosedPopups(closedPopups)
            actions.models.subscription.set(subscription)
            reducers.models.payment.update({ subscriptionProgress })
          })

          Promise.all([
            actions.models.user.fetchCommonData(awaitFor),
            actions.view.cosmeticsPage.fetchNotEmptyTypes(awaitFor),
          ])
            .then(awaitForResolve, awaitForReject)

          return awaitFor
        },
        (errorData) => handleRejectUpdateToken({
          ...errorData,
          tokenName: constants.requestTokens.googleSignin,
        })
      )
      .then(() => bindCouponToUser())
      .finally(() => actions.models.ui.hideRequestLoader())
  }

  return request.post('rest/google/signin', {
    body: { ...body, expiresIn },
    tokenName: constants.requestTokens.googleSignin,
    onComplete: () => actions.models.ui.hideRequestLoader(),
  })
    .then((result) => {
      const { mixpanelId, user: userId, isJustRegistered } = result

      if (isJustRegistered) {
        snowplow.track(constants.snowplowEvents.signup, { type: 'google' }, false)
      }

      setUserContext({ userId, mixpanelId })

      return result
    })
    .then((result) => {
      const { isJustRegistered } = result

      const [ awaitFor, awaitForResolve, awaitForReject ] = deferredPromise()

      Promise.all([
        actions.models.user.fetchUserData(awaitFor),
        actions.models.cart.addSubscription(false),
        !isJustRegistered ? actions.models.payment.fetchPostponedSubscriptionProcessing() : null,
      ])
        .then(awaitForResolve, awaitForReject)

      return awaitFor
    })
    .then(() => bindCouponToUser())
}

/**
 * Request password restore link to passed email
 * [Security] This method always return status 200. Check SCNT-5134
 *
 * @param {String} email
 * @param {Object} options
 * @param {Boolean} options.withErrorModal
 * @returns {Promise} request
 */
const remindPassword = (email, options = {}) =>
  request.post('rest/user/password/restore', {
    body: {
      email: email.trim().toLowerCase(),
    },
    ...options,
  })

/**
 * Reset password action
 *
 * @param {String} body.token
 * @param {String} body.newPassword
 * @param {String} body.passwordStrength
 * @returns {Promise} request
 */
const resetPassword = (body) =>
  request.post('rest/user/password/restore/finish', {
    body,
    withErrorModal: false,
  })

/**
 * Check password reset token
 *
 * @param {String} token
 * @returns {Promise} request
 */
const checkResetPasswordToken = (token) =>
  request.post('rest/user/password/restore/validate', {
    body: { token },
    withErrorModal: false,
  })

/**
 * Get user email and gender who registered from lead paage
 *
 * @returns {Promise} request
 */
const getLeadInfo = () =>
  request.get('rest/lead/info')
    .then((result) => {
      actions.misc.cookies.setItem('leadInfo', result)
      return result
    })

const resolveAllPostponedItems = ({ isNotificationVisible, skipLimitedItems } = {}) =>
  Promise.all([
    actions.models.fragrance.resolvePostponedStockNotificationSubscription(),
    actions.models.queue.resolveAllPostponedItems({ isNotificationVisible }),
    !skipLimitedItems && actions.models.queue.resolveAllPostponedLimitedItems(),
    actions.models.cart.resolvePostponedItem(),
  ])

const setAuthEmail = (email) => {
  reducers.models.user.setAuthEmail(email)
}

export default {
  login,
  register,
  googleAuth,
  getLeadInfo,
  setAuthEmail,
  facebookAuth,
  fetchUserData,
  resetPassword,
  setUserContext,
  remindPassword,
  fetchCommonData,
  bindCouponToUser,
  handleMixpanelId,
  trackRegisterFail,
  checkExistByEmail,
  setNewrelicContext,
  setUserIdForGoogle,
  checkResetPasswordToken,
  resolveAllPostponedItems,
}
